/**
 * Shared Report Service
 *
 * Backend-only service for report sharing endpoints.
 * Uses httpClient + apiPath following existing service patterns.
 */

import { apiPath } from '../config/env';
import { httpRequest } from './httpClient';
import { authManager } from './authManager';
import { normalizeEmail } from '../utils/emailUtils';
import type { ApiFailure } from './httpClient';
import type {
    ShareReportResponse,
    SharedReportDetail,
    SaveSharedReportResponse,
    SharedWithMeResponse,
} from '../types/shared';

const BASE = 'report-sharing';

/**
 * POST /api/v1/report-sharing/share
 * Owner shares a report — returns a shareable URL.
 */
export async function createShareLink(reportId: string): Promise<ShareReportResponse> {
    const email = normalizeEmail(authManager.getUserEmail());
    const result = await httpRequest<ShareReportResponse>(apiPath(`${BASE}/share`), {
        method: 'POST',
        body: JSON.stringify({ user_email: email, report_id: reportId }),
    });
    if (!result.success) {
        throw new Error((result as ApiFailure).error.message || 'Failed to create share link');
    }
    return result.data;
}

/**
 * GET /api/v1/report-sharing/shared/{reportId}?user_email=...
 * Fetch a shared report (any authenticated user).
 */
export async function getSharedReport(reportId: string): Promise<SharedReportDetail> {
    const email = normalizeEmail(authManager.getUserEmail());
    const result = await httpRequest<SharedReportDetail>(
        apiPath(`${BASE}/shared/${reportId}?user_email=${encodeURIComponent(email)}`),
        { method: 'GET' },
    );
    if (!result.success) {
        throw new Error((result as ApiFailure).error.message || 'Failed to fetch shared report');
    }
    return result.data;
}

/**
 * POST /api/v1/report-sharing/shared/{reportId}/save
 * Save a shared report to the recipient's account.
 */
export async function saveSharedReport(reportId: string): Promise<SaveSharedReportResponse> {
    const email = normalizeEmail(authManager.getUserEmail());
    const result = await httpRequest<SaveSharedReportResponse>(
        apiPath(`${BASE}/shared/${reportId}/save`),
        {
            method: 'POST',
            body: JSON.stringify({ user_email: email }),
        },
    );
    if (!result.success) {
        throw new Error((result as ApiFailure).error.message || 'Failed to save shared report');
    }
    return result.data;
}

/**
 * GET /api/v1/report-sharing/shared-with-me?user_email=...&limit=50&offset=0
 * List reports shared with the current user.
 */
export async function getSharedWithMe(
    limit = 50,
    offset = 0,
): Promise<SharedWithMeResponse> {
    const email = normalizeEmail(authManager.getUserEmail());
    const params = new URLSearchParams({
        user_email: email,
        limit: String(limit),
        offset: String(offset),
    });
    const result = await httpRequest<SharedWithMeResponse>(
        apiPath(`${BASE}/shared-with-me?${params.toString()}`),
        { method: 'GET' },
    );
    if (!result.success) {
        throw new Error((result as ApiFailure).error.message || 'Failed to fetch shared reports');
    }
    return result.data;
}

/**
 * DELETE /api/v1/report-sharing/shared/{reportId}?user_email=...
 * Remove a shared report from the recipient's list.
 */
export async function deleteSharedReport(reportId: string): Promise<void> {
    const email = normalizeEmail(authManager.getUserEmail());
    const result = await httpRequest<void>(
        apiPath(`${BASE}/shared/${reportId}?user_email=${encodeURIComponent(email)}`),
        { method: 'DELETE' },
    );
    if (!result.success) {
        throw new Error((result as ApiFailure).error.message || 'Failed to delete shared report');
    }
}

export const sharedReportService = {
    createShareLink,
    getSharedReport,
    saveSharedReport,
    getSharedWithMe,
    deleteSharedReport,
};
